// Builds the production-deviation workflow view from an engine case: the engine's state, audit and active-step form,
// IO's Dossier for the candidates the agent explained, and the legacy record for the deviation summary.
import {
  ProductionDeviationActionItem,
  ProductionDeviationAssessment,
  ProductionDeviationAuditEntry,
  ProductionDeviationAuditKind,
  ProductionDeviationCandidate,
  ProductionDeviationCase,
  ProductionDeviationCompletion,
  ProductionDeviationFindings,
  ProductionDeviationOutcome,
  ProductionDeviationRole,
  ProductionDeviationStep,
  ProductionDeviationVerdict,
  WorkflowItem,
} from '#models';
import type { ComposedCase, DossierCandidate } from '#services';
import { AuditTrailEntry } from '../../../../services/workflow-engine/generated/models/audit-trail-entry';
import { StepForm } from '../../../../services/workflow-engine/generated/models/step-form';
import { priorOwnerOid } from '../engine/engine-case.mapper';
import { PRODUCTION_DEVIATION_ROLE_LABELS, PRODUCTION_DEVIATION_STEP_LABELS } from './production-deviation-workflow.mapper';

const STEP_BY_KEY: Record<string, ProductionDeviationStep> = {
  confirm_findings: ProductionDeviationStep.ConfirmFindings,
  approve_findings: ProductionDeviationStep.ApproveFindings,
  execute_actions: ProductionDeviationStep.ExecuteActions,
  end_findings: ProductionDeviationStep.Closed,
};

const ROLE_BY_ENGINE_ROLE: Record<string, ProductionDeviationRole> = {
  'io-analyst': ProductionDeviationRole.IoAnalyst,
  'io-production-engineer': ProductionDeviationRole.IoProductionEngineer,
  'io-field-specialist': ProductionDeviationRole.IoFieldSpecialist,
};

/** The UC1 class's concrete role for a view role, as a `role_ref` capture expects it. */
export const ENGINE_ROLE_BY_ROLE: Partial<Record<ProductionDeviationRole, string>> = Object.fromEntries(
  Object.entries(ROLE_BY_ENGINE_ROLE).map(([engineRole, role]) => [role, engineRole]),
);

const OUTCOMES: readonly ProductionDeviationOutcome[] = ['dismissed', 'all_done', 'incomplete'];
const ASSESSMENTS = Object.values(ProductionDeviationAssessment) as string[];
const EXECUTE_ACTIONS = 'execute_actions';

type NameOf = (oid: string | null | undefined) => string | null;

type Json = Record<string, unknown>;

function asObject(value: unknown): Json | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}

function assessmentOf(value: unknown): ProductionDeviationAssessment | null {
  return typeof value === 'string' && ASSESSMENTS.includes(value) ? (value as ProductionDeviationAssessment) : null;
}

function toCandidate(candidate: DossierCandidate): ProductionDeviationCandidate {
  const explanation = candidate.explanation;
  const confidence = explanation['confidence'];
  return {
    candidateId: candidate.candidateId,
    rank: candidate.rank,
    title: asString(explanation['title']) ?? candidate.candidateId,
    family: assessmentOf(explanation['family']) ?? ProductionDeviationAssessment.Undetermined,
    confidence: typeof confidence === 'number' ? confidence : 0,
    rationale: asString(explanation['rationale']) ?? '',
  };
}

/**
 * The action items in the order the web API wrote them into the snapshot: each candidate's actions by rank, numbered
 * `a1`, `a2`, ... (CaseContextSnapshotMapper). Case-wide actions are not kept in the Dossier, so they appear only once
 * the engine has resolved the selection.
 */
function dossierActionItems(candidates: readonly DossierCandidate[]): ProductionDeviationActionItem[] {
  const items: ProductionDeviationActionItem[] = [];
  for (const candidate of candidates) {
    const actions = candidate.explanation['recommendedActions'];
    for (const action of Array.isArray(actions) ? actions : []) {
      const fields = asObject(action);
      items.push({
        id: `a${items.length + 1}`,
        candidateId: candidate.candidateId,
        targetRole: ProductionDeviationRole.IoFieldSpecialist,
        description: asString(fields?.['description']) ?? '',
        completionCriteria: asStrings(fields?.['completionCriteria']),
      });
    }
  }
  return items;
}

/** The payload of each `step-record` entry, in occurrence order. */
function records(entries: readonly AuditTrailEntry[]): { entry: AuditTrailEntry; payload: Json }[] {
  return entries
    .filter(entry => entry.eventKind === 'step-record')
    .map(entry => ({ entry, payload: asObject(entry.payload) ?? {} }));
}

function findingsFrom(entries: readonly AuditTrailEntry[]): ProductionDeviationFindings {
  const findings: ProductionDeviationFindings = {
    verdict: null,
    assessment: null,
    selectedCandidateIds: [],
    rejectionReason: null,
    defectOwner: null,
    defectDisposition: null,
  };
  for (const entry of entries) {
    if (entry.eventKind === 'step-entered' && entry.toStepKey === 'confirm_findings' && entry.outcomeToken === 'returned') {
      findings.verdict = null;
      findings.assessment = null;
      findings.selectedCandidateIds = [];
      findings.rejectionReason = null;
      findings.defectOwner = null;
      findings.defectDisposition = null;
    }
    if (entry.eventKind !== 'step-record') continue;
    const payload = asObject(entry.payload);
    if (!payload) continue;
    const captured = asObject(asObject(payload['captures'])?.['findings']);
    if (!captured) {
      continue;
    }
    const verdict = captured['verdict'];
    if (verdict === ProductionDeviationVerdict.Confirmed || verdict === ProductionDeviationVerdict.Rejected) {
      findings.verdict = verdict;
    }
    findings.assessment = assessmentOf(captured['assessment']) ?? findings.assessment;
    if ('rejectionReason' in captured) {
      findings.rejectionReason = asString(captured['rejectionReason']);
    }
    if ('selectedCandidates' in captured) {
      findings.selectedCandidateIds = asStrings(captured['selectedCandidates']);
    }
    const defect = asObject(captured['defect']);
    if (defect) {
      findings.defectOwner = ROLE_BY_ENGINE_ROLE[asString(defect['owner']) ?? ''] ?? null;
      findings.defectDisposition = asString(defect['disposition']);
    }
  }
  return findings;
}

/** The item ids the engine sealed when the case last entered execute_actions, or null before then. */
function sealedSelection(recorded: ReturnType<typeof records>): string[] | null {
  const sealed = recorded.filter(({ payload }) => payload['forEach'] === EXECUTE_ACTIONS).at(-1);
  return sealed ? asStrings(sealed.payload['items']) : null;
}

/** Gives the Dossier's items the ids the engine sealed, matched in order, when the two lists line up. */
function alignItems(
  items: ProductionDeviationActionItem[],
  sealed: string[] | null,
  selected: readonly string[],
): ProductionDeviationActionItem[] {
  if (!sealed) {
    return items;
  }
  const kept = items.filter(item => item.candidateId === null || selected.includes(item.candidateId));
  if (kept.length !== sealed.length) {
    return items;
  }
  const renamed = new Map(kept.map((item, index) => [item, sealed[index]]));
  return items.map(item => (renamed.has(item) ? { ...item, id: renamed.get(item)! } : item));
}

/** Adds the item the engine has active when the Dossier does not hold it, such as a case-wide action. */
function withActiveItem(items: ProductionDeviationActionItem[], form: StepForm | null): ProductionDeviationActionItem[] {
  const active = form?.item;
  if (!active?.id || items.some(entry => entry.id === active.id)) {
    return items;
  }
  return [
    ...items,
    {
      id: active.id,
      candidateId: null,
      targetRole: ROLE_BY_ENGINE_ROLE[active.targetRole ?? ''] ?? ProductionDeviationRole.IoFieldSpecialist,
      description: active.description ?? '',
      completionCriteria: active.completionCriteria,
    },
  ];
}

function completionsFrom(entries: readonly AuditTrailEntry[], nameOf: NameOf): Record<string, ProductionDeviationCompletion> {
  const completions: Record<string, ProductionDeviationCompletion> = {};
  const reasons = new Map<string, string | null>();
  for (const entry of entries) {
    const payload = asObject(entry.payload) ?? {};
    if (entry.eventKind === 'step-entered') {
      const decidedItemId = asString(payload['decidedItemId']);
      if (decidedItemId) {
        reasons.set(decidedItemId, asString(payload['reason']));
      }
    }
    if (entry.eventKind !== 'step-record') {
      continue;
    }
    const itemId = asString(payload['itemId']);
    const note = asString(asObject(asObject(payload['captures'])?.['action_completion'])?.['completionNote']);
    if (itemId && note !== null) {
      const token = entry.outcomeToken;
      const outcome = token === 'done' || token === 'not_applicable' || token === 'cannot_complete' ? token : null;
      completions[itemId] = {
        outcome, reason: reasons.get(itemId) ?? asString(payload['reason']),
        note, by: nameOf(entry.actorOid) ?? 'Unknown', atUtc: entry.occurredOnUtc,
      };
    }
  }
  return completions;
}

function stepLabel(stepKey: string | null): string {
  const step = stepKey ? STEP_BY_KEY[stepKey] : undefined;
  return step ? PRODUCTION_DEVIATION_STEP_LABELS[step] : (stepKey ?? 'the case');
}

function auditEntry(entry: AuditTrailEntry, index: number, nameOf: NameOf): ProductionDeviationAuditEntry | null {
  const payload = asObject(entry.payload) ?? {};
  const stepKey = entry.toStepKey ?? entry.fromStepKey;
  const step = (stepKey && STEP_BY_KEY[stepKey]) || ProductionDeviationStep.ConfirmFindings;
  const actor = entry.actorType === 'human' ? (nameOf(entry.actorOid) ?? 'Unknown') : 'Workflow engine';
  const owner = nameOf(entry.ownerOidAtTime);
  const reason = asString(payload['reason']);

  let kind: ProductionDeviationAuditKind;
  let text: string;
  switch (entry.eventKind) {
    case 'case-opened':
      kind = 'case-opened';
      text = `Case opened at ${stepLabel(entry.toStepKey)}.`;
      break;
    case 'owner-claimed':
      kind = 'owner-claimed';
      text = `Claimed ${stepLabel(stepKey).toLowerCase()}${entry.actorRole ? ` as ${entry.actorRole}` : ''}.`;
      break;
    case 'owner-released':
      kind = 'owner-returned';
      text = `Released ${stepLabel(stepKey).toLowerCase()} back to its queue${reason ? `: ${reason}` : '.'}`;
      break;
    case 'owner-returned':
      kind = 'owner-returned';
      text = `Returned to ${owner ?? 'the prior owner'}.`;
      break;
    case 'step-entered':
      kind = 'step-entered';
      text = `${entry.outcomeToken ? `${entry.outcomeToken}: ` : ''}entered ${stepLabel(entry.toStepKey).toLowerCase()}${reason ? ` (${reason})` : ''}.`;
      break;
    case 'step-record':
      if (payload['forEach'] === EXECUTE_ACTIONS) {
        kind = 'actions-resolved';
        text = `Resolved ${asStrings(payload['items']).length} action item(s) from the selected candidates.`;
      } else if (asString(payload['itemId'])) {
        kind = 'action-completed';
        text = `${entry.outcomeToken ?? 'decided'} action ${asString(payload['itemId'])}.`;
      } else {
        return null;
      }
      break;
    case 'case-closed':
      kind = 'case-closed';
      text = `Case closed${entry.outcomeToken ? ` as ${entry.outcomeToken}` : ''}.`;
      break;
    default:
      return null;
  }
  return { id: index + 1, atUtc: entry.occurredOnUtc, kind, actor, step, text };
}

/** Detection is when IO recorded the deviation; the engine case may open much later, so its open time is only a fallback. */
function detectedUtc(item: WorkflowItem, openedOnUtc: string): string {
  const created = item.createdDate;
  return created && Number.isFinite(Date.parse(created)) ? created : openedOnUtc;
}

/** The workflow view of an engine-backed production-deviation case. */
export function toProductionDeviationCase(
  composed: ComposedCase,
  form: StepForm | null,
  item: WorkflowItem,
  nameOf: NameOf,
): ProductionDeviationCase {
  const { state, audit, graph, dossier } = composed;
  const entries = audit.entries;
  const ranked = [...(dossier?.candidates ?? [])].sort((a, b) => a.rank - b.rank);
  const candidates = ranked.map(toCandidate);
  const recorded = records(entries);
  const findings = findingsFrom(entries);
  // A case on a class version without the capture kept the reason as the dismissing decision's reason.
  if (findings.rejectionReason === null && findings.verdict === ProductionDeviationVerdict.Rejected) {
    const dismissal = entries.filter(entry => entry.eventKind === 'step-entered' && entry.outcomeToken === 'dismissed').at(-1);
    findings.rejectionReason = asString(asObject(dismissal?.payload)?.['reason']);
  }
  const sealed = sealedSelection(recorded);

  const stepKey = state.activeStep?.stepKey ?? null;
  const step = state.status !== 'open'
    ? ProductionDeviationStep.Closed
    : (stepKey && STEP_BY_KEY[stepKey]) || ProductionDeviationStep.ConfirmFindings;
  const ownerRole = state.ownerOid && form?.role ? (ROLE_BY_ENGINE_ROLE[form.role] ?? null) : null;

  // A return re-enters confirm_findings with the approver's reason on that entry.
  const lastReturn = step === ProductionDeviationStep.ConfirmFindings
    ? entries.filter(entry => entry.eventKind === 'step-entered' && entry.toStepKey === 'confirm_findings').at(-1)
    : undefined;
  const returnReason = lastReturn?.outcomeToken === 'returned' ? asString(asObject(lastReturn.payload)?.['reason']) : null;

  const signalContext = asObject(dossier?.signals[0]?.context);
  // Older state reads omit the loop result; its terminal transition is still recorded in the audit.
  const ending = state.status !== 'open'
    ? entries.filter(entry => entry.eventKind === 'step-entered' && entry.toStepKey === 'end_findings').at(-1)
    : undefined;
  const outcome = OUTCOMES.find(value => value === state.outcome)
    ?? OUTCOMES.find(value => value === ending?.outcomeToken)
    ?? null;

  return {
    engineCaseId: state.caseNumber,
    workflowVersion: Number(state.classVersion),
    contentHash: graph.contentHash,
    step,
    owner: state.ownerOid ? (nameOf(state.ownerOid) ?? state.ownerOid) : null,
    ownerRole,
    priorOwner: nameOf(priorOwnerOid(entries, 'confirm_findings')),
    returnReason,
    dataForDate: item.dataForDate ?? asString(signalContext?.['dataForDate']) ?? state.openedOnUtc,
    detectedUtc: detectedUtc(item, state.openedOnUtc),
    context: {
      summary: item.summary ?? '',
      stream: item.identity?.stream ?? '',
      product: item.identity?.product ?? '',
      area: item.identity?.area ?? '',
    },
    candidates,
    actionItems: withActiveItem(alignItems(dossierActionItems(ranked), sealed, findings.selectedCandidateIds), form),
    proposedAssessment: candidates[0]?.family ?? ProductionDeviationAssessment.Undetermined,
    findings,
    resolvedItemIds: sealed,
    completions: completionsFrom(entries, nameOf),
    outcome,
    audit: entries
      .map((entry, index) => auditEntry(entry, index, nameOf))
      .filter((entry): entry is ProductionDeviationAuditEntry => entry !== null),
  };
}

/** The engine role for one of the UC1 class's concrete roles, or null for a role outside the class. */
export function roleFromEngineRole(role: string | null | undefined): ProductionDeviationRole | null {
  return (role && ROLE_BY_ENGINE_ROLE[role]) || null;
}

/** The label of a concrete engine role, falling back to the role name. */
export function engineRoleLabel(role: string | null | undefined): string {
  const mapped = role ? ROLE_BY_ENGINE_ROLE[role] : undefined;
  return mapped ? PRODUCTION_DEVIATION_ROLE_LABELS[mapped] : (role ?? '');
}
